import { isPlatformBrowser } from '@angular/common';
import {
  ErrorHandler,
  Injectable,
  NgZone,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Facebook as AppNativeFacebook } from '@awesome-cordova-plugins/facebook/ngx';
import { SocialLogin } from '@capgo/capacitor-social-login';
import type { SSOInterface, SSOResponseData, SSOUser } from '@freelancer/auth';
import type { ResponseData } from '@freelancer/freelancer-http';
import { Pwa } from '@freelancer/pwa';
import { SocialLoginPlugin } from '@freelancer/social-login-plugin';
import { isDefined } from '@freelancer/utils';
import { jwtDecode } from 'jwt-decode';
import { FACEBOOK_CONFIG } from './facebook.config';
import type {
  FacebookAuthenticationJWT,
  FacebookAuthResponse,
  FacebookLoginResponse,
} from './facebook.interface';
import { FacebookConfig, FacebookSignInError } from './facebook.interface';

@Injectable({
  providedIn: 'root',
})
export class Facebook
  implements SSOInterface<'facebook', FacebookAuthResponse>
{
  private config = inject<FacebookConfig>(FACEBOOK_CONFIG);
  private errorHandler = inject(ErrorHandler);
  private platformId = inject(PLATFORM_ID);
  private appNativeFacebook = inject(AppNativeFacebook);
  private ngZone = inject(NgZone);
  private pwa = inject(Pwa);
  private socialLoginPlugin = inject(SocialLoginPlugin);

  private readonly requiredPermissions = ['email', 'public_profile'];

  /**
   * An existing successful login state which we track
   * because the Facebook SDK complains if we call login multiple times
   */
  successfulLoginState?: ResponseData<
    FacebookAuthResponse,
    FacebookSignInError
  > & {
    provider: 'facebook';
  };

  async isEnabled(): Promise<boolean> {
    return isPlatformBrowser(this.platformId) && isDefined(this.config.appId);
  }

  get appId(): string | undefined {
    return this.config.appId;
  }

  async getUserDetails(
    userAuthResponse: FacebookAuthResponse,
  ): Promise<SSOUser | undefined> {
    if (!(await this.isEnabled())) {
      return undefined;
    }

    if (userAuthResponse.authenticationToken) {
      // authenticationToken is returned by the Limited Login API
      // which doesn't allow querying the API for further responses.
      // All the data we care about is instead inside the token (JWT)
      const {
        email,
        given_name: name,
        family_name: lastName,
        picture: profileUrl,
      } = jwtDecode<FacebookAuthenticationJWT>(
        userAuthResponse.authenticationToken,
      );

      return {
        email,
        name,
        lastName,
        profileUrl,
      };
    }

    const userPromise = this.callAPI(
      `/${userAuthResponse.userID}?fields=first_name,email,last_name`,
    );
    const picPromise = this.callAPI(
      `/${userAuthResponse.userID}/picture?redirect=false`,
    );
    const userResponse = await userPromise;
    const picResponse = await picPromise;

    if (userResponse && !userResponse.error && userResponse.first_name) {
      return {
        email: userResponse.email,
        name: userResponse.first_name,
        lastName: userResponse.last_name,
        // don't block on not being able to fetch the profile pic
        profileUrl:
          picResponse && !picResponse.error ? picResponse.data.url : undefined,
      };
    }
  }

  async login(): Promise<
    SSOResponseData<'facebook', FacebookAuthResponse, FacebookSignInError>
  > {
    if (!(await this.isEnabled())) {
      return {
        provider: 'facebook',
        status: 'error',
        errorCode: FacebookSignInError.UNKNOWN,
      };
    }

    if (this.successfulLoginState) {
      return this.successfulLoginState;
    }

    let fbResponse: FacebookLoginResponse;
    if (this.pwa.isNative()) {
      fbResponse = this.socialLoginPlugin.isSupported()
        ? await this.loginViaCapacitorSocialLogin()
        : await this.loginViaCordovaFacebook();
    } else {
      fbResponse = await new Promise((resolve, reject) => {
        this.ngZone.runOutsideAngular(() => {
          window.FB.login(
            (response: fb.StatusResponse) => {
              this.ngZone.run(() => {
                resolve(response);
              });
            },
            {
              scope: this.requiredPermissions.join(','),
            },
          );
        });
      });
    }

    if (fbResponse.status === 'connected' && fbResponse.authResponse) {
      this.successfulLoginState = {
        provider: 'facebook',
        status: 'success',
        result: fbResponse.authResponse,
      };
      return this.successfulLoginState;
    }

    // convert string union into enum
    let errorCode: FacebookSignInError;
    switch (fbResponse.status) {
      // desktop
      case 'authorization_expired':
        errorCode = FacebookSignInError.EXPIRED;
        break;
      // desktop
      case 'not_authorized':
        errorCode = FacebookSignInError.NOT_AUTHORIZED;
        break;
      // mobile
      case 'User cancelled.':
        errorCode = FacebookSignInError.CANCELED;
        break;
      default:
        errorCode = FacebookSignInError.UNKNOWN;
    }

    return {
      provider: 'facebook',
      status: 'error',
      errorCode,
    };
  }

  private async loginViaCapacitorSocialLogin(): Promise<FacebookLoginResponse> {
    // iOS: Limited Login (matches ATT-disabled behaviour from the cordova path).
    // Android: full login.
    const isLimitedLogin = this.pwa.getPlatform() === 'ios';
    const nonce = isLimitedLogin
      ? Math.random().toString(36).slice(2)
      : undefined;
    try {
      await this.socialLoginPlugin.initialize();
      const res = await SocialLogin.login({
        provider: 'facebook',
        options: {
          permissions: [...this.requiredPermissions],
          limitedLogin: isLimitedLogin,
          ...(nonce !== undefined ? { nonce } : {}),
        },
      });
      if (res.provider !== 'facebook') {
        return { status: 'unknown' };
      }

      const { result } = res;
      const userID = (result.profile as { userID?: string } | undefined | null)
        ?.userID;
      if (userID === undefined || userID === '') {
        return { status: 'unknown' };
      }

      // capacitor-social-login returns "" rather than null. Set it to null before sending to backend.
      const nonEmpty = (v: string | null | undefined): string | undefined =>
        v === undefined || v === null || v === '' ? undefined : v;
      const accessToken = nonEmpty(result.accessToken?.token);
      const authenticationToken = nonEmpty(result.idToken);

      return {
        status: 'connected' as const,
        authResponse: {
          userID,
          nonce,
          // facebookLogin.php requires EXACTLY ONE of accessToken or authenticationToken.
          // Prefer standard-login token which contains more info
          ...(accessToken !== undefined
            ? { accessToken } // standard login
            : { authenticationToken }), // limited login
        },
      };
    } catch (err) {
      const message = (err as { message?: string })?.message ?? '';
      if (message.includes('canceled') || message.includes('cancelled')) {
        return { status: 'User cancelled.' };
      }
      return { status: 'unknown' };
    }
  }

  // TODO: T338591 Keeping this for backward compatibility with old native builds
  // still shipped with cordova-plugin-fbsdk. Remove once native adoption completes.
  private async loginViaCordovaFacebook(): Promise<FacebookLoginResponse> {
    // iOS SDK requires limited login when App Tracking Transparency is disabled
    // This check should probably use the TrackingConsent service, but we
    // disable third-party tracking on Android as well (no reason not to)
    // whereas we can still use the non-limited login there.
    if (this.pwa.getPlatform() === 'ios') {
      const nonce = Math.random().toString(36).slice(2);
      return this.appNativeFacebook
        .loginWithLimitedTracking(this.requiredPermissions, nonce)
        .then(response => ({
          status: response.status,
          authResponse: {
            authenticationToken: response.authResponse?.authenticationToken,
            nonce: response.authResponse?.nonce,
            userID: response.authResponse?.userID,
          },
        }))
        .catch(status => ({
          status,
        }));
    }
    // Android SDK doesn't force limited login:
    return this.appNativeFacebook
      .login(this.requiredPermissions)
      .then((response: any) => ({
        status: response.status,
        authResponse: {
          accessToken: response.authResponse?.accessToken,
          userID: response.authResponse?.userID,
        },
      }))
      .catch(status => ({
        status,
      }));
  }

  async loadLoginStatus(): Promise<boolean> {
    if (this.pwa.isNative()) {
      if (this.socialLoginPlugin.isSupported()) {
        try {
          await this.socialLoginPlugin.initialize();
          const { isLoggedIn } = await SocialLogin.isLoggedIn({
            provider: 'facebook',
          });
          if (!isLoggedIn) {
            this.successfulLoginState = undefined;
          }
        } catch (err) {
          this.errorHandler.handleError(err);
        }
        // always resolve true
        return true;
      }
      // native apps have the SDK built in and don't need to load the JS one
      // we can immediately fetch the login status
      return this.appNativeFacebook.getLoginStatus().then((status: any) => {
        if (status.status === 'connected') {
          this.successfulLoginState = {
            provider: 'facebook',
            status: 'success',
            result: status.authResponse,
          };
        } else {
          this.successfulLoginState = undefined;
        }
        // always resolve true
        return true;
      });
    }

    const enabled = await this.isEnabled();

    return new Promise((resolve, reject) => {
      const { appId } = this.config;
      if (!appId || !enabled) {
        resolve(false);
        return;
      }
      const fbGetLoginStatus = (): void => {
        // fetch the login status before resolving initialisation
        // this prevents popups from being blocked when we call `login` later.
        this.ngZone.runOutsideAngular(() => {
          window.FB.getLoginStatus(status => {
            this.ngZone.run(() => {
              if (status.status === 'connected') {
                this.successfulLoginState = {
                  provider: 'facebook',
                  status: 'success',
                  result: status.authResponse,
                };
              } else {
                this.successfulLoginState = undefined;
              }
              resolve(true);
            });
            // Force a "roundtrip" to bypass the SDK's local cache.
            // See. T211548
          }, true);
        });
      };
      // We don't know yet if the FB SDK has been loaded here
      if ((window.FB as fb.FacebookStatic | undefined)?.getLoginStatus) {
        fbGetLoginStatus();
      } else {
        window.fbAsyncInit = () => {
          window.FB.init({
            appId,
            autoLogAppEvents: true,
            xfbml: false,
            cookie: false,
            status: true,
            version: 'v18.0',
          });
          fbGetLoginStatus();
        };
        if (document.getElementById('external-fb-sdk')) {
          return;
        }
        const e = document.createElement('script');
        e.src = 'https://connect.facebook.net/en_US/sdk.js';
        e.id = 'external-fb-sdk';
        e.onerror = err => {
          console.error('Failed to load Facebook SDK', err);
          resolve(false);
        };
        const s = document.getElementsByTagName('script')[0];
        (s.parentNode as Node).insertBefore(e, s);
      }
    });
  }

  /**
   * Make a facebook graph API call with the current auth state
   **/
  private async callAPI(endpoint: string): Promise<any> {
    const authResponse = this.successfulLoginState;
    if (!authResponse || authResponse.status === 'error') {
      return { error: true };
    }

    if (this.pwa.isNative()) {
      // Use capacitor social login plugin
      if (this.socialLoginPlugin.isSupported()) {
        // social login doesn't wrap Graph API — call it directly with the accessToken.
        const { accessToken } = authResponse.result;
        if (accessToken === undefined) {
          return { error: true };
        }
        const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
        const sep = path.includes('?') ? '&' : '?';
        // TODO: T339829 Bump v18.0 to current stable Graph API version
        const url = `https://graph.facebook.com/v18.0${path}${sep}access_token=${encodeURIComponent(accessToken)}`;
        try {
          const response = await fetch(url);
          if (!response.ok) {
            // report error to Sentry
            const data = await response.json().catch(() => undefined);
            this.errorHandler.handleError(
              new Error(
                `Facebook Graph API ${response.status} for ${endpoint}`,
                { cause: data },
              ),
            );
            return { error: true };
          }
          return await response.json();
        } catch {
          return { error: true };
        }
      }
      // Use cordova plugin
      return this.appNativeFacebook
        .api(
          endpoint,
          [], // no extra permissions needed
        )
        .catch(() => ({ error: true }));
    }

    return new Promise<any>((resolve, reject) => {
      this.ngZone.runOutsideAngular(() => {
        window.FB.api(
          endpoint,
          {
            redirect: false,
            access_token: authResponse.result.accessToken,
          },
          response => {
            this.ngZone.run(() => {
              resolve(response);
            });
          },
        );
      });
    });
  }

  logout(): Promise<void> {
    return this.loadLoginStatus().then(() => {
      if (this.successfulLoginState) {
        window.FB.logout();
      }
    });
  }
}
